import { Component, OnInit } from '@angular/core';
import { ProfessionServicesService } from '../services/profession-services.service';
import { UploadServiceService } from '../service/upload-service.service';
import { firstValueFrom } from 'rxjs';

const BEHAVIOR_TYPE_ID = 'evaluator-test-tbd-1';

interface OptionCategory {
  categoryId?: string;
  subcategoryId: string;
  categoryName: string;
  categoryDescription?: string;
  'subcatId-mapping'?: string;
  'subcategoryId-mapping'?: string;
  option?: string;
}

interface BehaviorSubtype {
  typeId: string;
  subTypeId: string;
  subType: string;
}

@Component({
  selector: 'app-behavior-categories-manager',
  standalone: false,
  templateUrl: './behavior-categories-manager.component.html',
  styleUrl: './behavior-categories-manager.component.css'
})

export class BehaviorCategoriesManagerComponent implements OnInit {
  // State
  categories: OptionCategory[] = [];
  selectedCategory: OptionCategory | null = null;
  editedCategory: OptionCategory | null = null;
  originalCategory: OptionCategory | null = null;
  subTypeList: BehaviorSubtype[] = [];
  
  // UI State
  isLoading = false;
  isLoadingSubtypes = false;
  isSaving = false;
  isNewCategory = false;
  showDeleteConfirmation = false;

  constructor(
    private professionService: ProfessionServicesService,
    private uploadService: UploadServiceService
  ) {}

  ngOnInit(): void {
    this.loadBehaviorSubtypes();
    this.loadCategories();
  }

  /**
   * Load behavioral subtypes for the mapping dropdown
   */
  async loadBehaviorSubtypes(): Promise<void> {
    this.isLoadingSubtypes = true;
    try {
      const data: any = await firstValueFrom(
        this.uploadService.getAllSubtypesByType(BEHAVIOR_TYPE_ID)
      );

      this.subTypeList = data?.resp || [];
    } catch (error) {
      console.error('Error loading behavior subtypes:', error);
      this.subTypeList = [];
      alert('Failed to load behavior subtypes. Mapping dropdown may be empty.');
    } finally {
      this.isLoadingSubtypes = false;
    }
  }

  /**
   * Load all categories from the API
   */
  async loadCategories(): Promise<void> {
    this.isLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.professionService.getBehaviorOptionCategories()
      );
  
      if (response?.success && response?.allBehaviorCategories) {
        this.categories = (response.allBehaviorCategories || []).map((category: OptionCategory) =>
          this.normalizeOptionCategory(category)
        );
      } else {
        this.categories = [];
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Failed to load categories. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Select a category to edit
   */
  selectCategory(category: OptionCategory): void {
    if (this.hasUnsavedChanges()) {
      const confirmDiscard = confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirmDiscard) {
        return;
      }
    }

    const normalizedCategory = this.normalizeOptionCategory(category);
    this.selectedCategory = normalizedCategory;
    this.editedCategory = { ...normalizedCategory };
    this.originalCategory = { ...normalizedCategory };
    this.isNewCategory = false;
  }

  /**
   * Open dialog to create a new category
   */
  openNewCategoryDialog(): void {
    if (this.hasUnsavedChanges()) {
      const confirmDiscard = confirm('You have unsaved changes. Do you want to discard them?');
      if (!confirmDiscard) {
        return;
      }
    }

    const newCategory: OptionCategory = {
      subcategoryId: '',
      categoryName: '',
      categoryDescription: '',
      'subcatId-mapping': '',
      option: ''
    };

    this.selectedCategory = newCategory;
    this.editedCategory = { ...newCategory };
    this.originalCategory = { ...newCategory };
    this.isNewCategory = true;
  }

  /**
   * Save category (create or update)
   */
  async saveCategory(): Promise<void> {
    if (!this.isFormValid() || !this.editedCategory) return;
  
    this.isSaving = true;
    try {
      const payload = {
        flow_selected: 'create-edit-option-category',
        subcategoryId: this.editedCategory.subcategoryId.trim(),
        categoryName: this.editedCategory.categoryName.trim(),
        categoryDescription: this.editedCategory.categoryDescription?.trim() || '',
        'subcatId-mapping': this.getMappedSubcategoryId(this.editedCategory).trim(),
        option: this.editedCategory.option?.trim() || '',
        ...(this.editedCategory.categoryId && { categoryId: this.editedCategory.categoryId })
      };
  
      const response: any = await firstValueFrom(
        this.professionService.createOrUpdateBehaviorOptionCategory(payload)
      );
  
      if (!response?.success) throw new Error('Unable to save category');
  
      // reload and WAIT for it
      await this.loadCategories();
  
      // select the saved category using the correct id
      const savedId = response?.optionCategory?.categoryId;
      if (savedId) {
        const savedCategory = this.categories.find(c => c.categoryId === savedId);
        if (savedCategory) this.selectCategory(savedCategory);
      }
  
      alert(this.isNewCategory ? 'Category created successfully!' : 'Category updated successfully!');
      this.isNewCategory = false;
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }
  

  /**
   * Delete category
   */
  deleteCategory(): void {
    this.showDeleteConfirmation = true;
  }

  /**
   * Confirm and execute category deletion
   */
  async confirmDelete(): Promise<void> {
    const categoryId = this.selectedCategory?.categoryId;
    const subcategoryId = this.selectedCategory?.subcategoryId;
    
    if (!categoryId || !subcategoryId) return;
  
    this.isSaving = true;
    try {
      const response: any = await firstValueFrom(
        this.professionService.deleteBehaviorOptionCategory(categoryId, subcategoryId)
      );
  
      if (!response?.success) throw new Error('Unable to delete category');
  
      // Close the confirmation dialog
      this.showDeleteConfirmation = false;
  
      // Clear selection
      this.selectedCategory = null;
      this.editedCategory = null;
      this.originalCategory = null;
      this.isNewCategory = false;
  
      // Reload categories and wait for it
      await this.loadCategories();
  
      alert('Category deleted successfully!');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category. Please try again.');
      this.showDeleteConfirmation = false;
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Cancel editing and revert changes
   */
  cancelEditing(): void {
    if (this.isNewCategory) {
      this.selectedCategory = null;
      this.editedCategory = null;
      this.originalCategory = null;
      this.isNewCategory = false;
    } else if (this.originalCategory) {
      this.editedCategory = { ...this.originalCategory };
    }
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    if (!this.editedCategory || !this.originalCategory) {
      return false;
    }

    return (
      this.editedCategory.subcategoryId !== this.originalCategory.subcategoryId ||
      this.editedCategory.categoryName !== this.originalCategory.categoryName ||
      (this.editedCategory.categoryDescription || '') !== (this.originalCategory.categoryDescription || '') ||
      this.getMappedSubcategoryId(this.editedCategory) !== this.getMappedSubcategoryId(this.originalCategory) ||
      (this.editedCategory.option || '') !== (this.originalCategory.option || '')
    );
  }

  /**
   * Check if the form is valid
   */
  isFormValid(): boolean {
    if (!this.editedCategory) {
      return false;
    }

    // Both subcategoryId and categoryName are required and must not be empty
    return !!(
      this.editedCategory.subcategoryId && 
      this.editedCategory.subcategoryId.trim().length > 0 &&
      this.editedCategory.categoryName && 
      this.editedCategory.categoryName.trim().length > 0
    );
  }

  private normalizeOptionCategory(category: OptionCategory): OptionCategory {
    const mappedSubcategoryId = this.getMappedSubcategoryId(category);

    return {
      ...category,
      'subcatId-mapping': mappedSubcategoryId,
      option: category.option || ''
    };
  }

  private getMappedSubcategoryId(category: OptionCategory): string {
    return category['subcatId-mapping'] || category['subcategoryId-mapping'] || '';
  }
}
